import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getSettings, logPageVisit, rechercheFiche, getMonRole } from './services/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Techniciens from './pages/Techniciens';
import CompteRendus from './pages/CompteRendus';
import CRDetail from './pages/CRDetail';
import Stats from './pages/Stats';
import Planning from './pages/Planning';
import Comptes from './pages/Comptes';
import Recherche from './pages/Recherche';
import SAV from './pages/SAV';
import ImportCovage from './pages/ImportCovage';
import Planification from './pages/Planification';
import Logs from './pages/Logs';
import SuiviCRI from './pages/SuiviCRI';
import SuiviTerrain from './pages/SuiviTerrain';
import Settings from './pages/Settings';
import SMS from './pages/SMS';
import Emails from './pages/Emails';
import CommandeONT from './pages/CommandeONT';
import Agenda from './pages/Agenda';
import PiecesJointes from './pages/PiecesJointes';
import Workflow from './pages/Workflow';
import KPI from './pages/KPI';
import Qualifications from './pages/Qualifications';
import EmailLog from './pages/EmailLog';
import Remunerations from './pages/Remunerations';
import StockONT from './pages/StockONT';
import Secteurs from './pages/Secteurs';
import Absences from './pages/Absences';
import ERDV from './pages/ERDV';
import Dossier from './pages/Dossier';
import Terrain from './pages/Terrain';
import Malus from './pages/Malus';
import PortailCovage from './pages/PortailCovage';
import DevisSansMontant from './pages/DevisSansMontant';
import Alertes from './components/Alertes';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const SettingsContext = createContext({});
export const useSettings = () => useContext(SettingsContext);

function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const reload = useCallback(() => {
    getSettings().then(setSettings).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <SettingsContext.Provider value={{ settings, reloadSettings: reload }}>
      {children}
    </SettingsContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => {
    const t = localStorage.getItem('admin_token');
    const n = localStorage.getItem('admin_name');
    const r = localStorage.getItem('admin_role');
    const p = localStorage.getItem('admin_pages');
    const rn = localStorage.getItem('admin_role_name');
    return t ? { token: t, name: n, role: r || 'admin', pages: p ? JSON.parse(p) : null, role_name: rn || '' } : null;
  });
  const signIn = (token, name, key, role = 'admin', pages = null, role_name = '') => {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_name', name);
    localStorage.setItem('admin_key', key);
    localStorage.setItem('admin_role', role);
    localStorage.setItem('admin_role_name', role_name);
    if (pages) localStorage.setItem('admin_pages', JSON.stringify(pages));
    else localStorage.removeItem('admin_pages');
    setAdmin({ token, name, role, pages, role_name });
  };
  // Le role, relu a la source plutot que cru sur parole.
  //
  // Le login etait le seul moment ou le serveur donnait la liste des pages, et
  // le panel la gardait sans jamais la redemander : ajouter une page a un role
  // ne changeait rien pour qui etait deja connecte, et rien ne le signalait.
  // On la relit a l'ouverture du panel, et au retour sur l'onglet — le panel
  // reste ouvert la journee, c'est la que le changement a le plus de chances
  // d'etre arrive entre-temps.
  useEffect(() => {
    if (!localStorage.getItem('admin_token')) return undefined;
    let vivant = true;
    const relire = () => {
      getMonRole().then(d => {
        if (!vivant) return;
        const pages = d.pages || null;
        const role = d.role || 'admin';
        const nom = d.role_name || '';
        if (pages) localStorage.setItem('admin_pages', JSON.stringify(pages));
        else localStorage.removeItem('admin_pages');
        localStorage.setItem('admin_role', role);
        localStorage.setItem('admin_role_name', nom);
        setAdmin(a => {
          // Rien de change : rendre le meme objet evite de redessiner tout le
          // panel a chaque retour sur l'onglet.
          if (!a) return a;
          if (a.role === role && a.role_name === nom
              && JSON.stringify(a.pages || null) === JSON.stringify(pages)) return a;
          return { ...a, role, pages, role_name: nom };
        });
      }).catch(() => {});  // reseau coupe : on garde ce qu'on avait
    };
    relire();
    const auRetour = () => { if (document.visibilityState === 'visible') relire(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => { vivant = false; document.removeEventListener('visibilitychange', auRetour); };
  }, [admin?.token]);

  const signOut = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('admin_key');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_pages');
    localStorage.removeItem('admin_role_name');
    setAdmin(null);
  };
  return <AuthContext.Provider value={{ admin, signIn, signOut }}>{children}</AuthContext.Provider>;
}

const ICONS = {
  dossier: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/></svg>,
  erdv: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h8V3a1 1 0 112 0v1h1a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1zM3 8v8h14V8H3zm2 2h3v3H5v-3zm5 0h3v3h-3v-3z" clipRule="evenodd"/></svg>,
  absences: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm7.07 7.93a1 1 0 00-1.414-1.415L10 10.172 8.344 8.515A1 1 0 106.93 9.93l1.656 1.657-1.657 1.656a1 1 0 101.415 1.415L10 13l1.657 1.657a1 1 0 101.414-1.415l-1.657-1.656 1.657-1.657z" clipRule="evenodd"/></svg>,
  secteurs: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>,
  dashboard: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"/><path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"/></svg>,
  users: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>,
  docs: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/></svg>,
  stats: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>,
  planning: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/></svg>,
  planif: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>,
  accounts: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v1h-3zM4.75 14.094A5.973 5.973 0 004 17v1H1v-1a3 3 0 013.75-2.906z"/></svg>,
  search: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/></svg>,
  sav: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/></svg>,
  sms: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/><path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/></svg>,
  mail: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>,
  import: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>,
  pj: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd"/></svg>,
  workflow: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/></svg>,
  kpi: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>,
  remun: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/></svg>,
  stock: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"/><path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd"/></svg>,
  logout: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"/></svg>,
  logs: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/></svg>,
  cri: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>,
  settings: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/></svg>,
  devis: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h5.586A2 2 0 0113 2.586L15.414 5A2 2 0 0116 6.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm4 6a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm1 3a1 1 0 100 2h2a1 1 0 100-2H9z" clipRule="evenodd"/></svg>,
  guichet: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>,
  malus: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.1c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>,
  ousontils: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>,
  terrain: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>,
  ont: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5zm2 2v2h8V7H6zm0 4v2h5v-2H6z"/></svg>,
  emaillog: <svg viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/><circle cx="15.5" cy="15.5" r="4.5" fill="#22c55e"/><path d="M13.5 15.5l1.5 1.5 2.5-2.5" stroke="#fff" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  agenda: <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6zm0 4a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd"/></svg>,
};

/**
 * Les pages du panel, en une seule liste.
 *
 * Elle servait deux fois, écrite deux fois : une pour le menu, une pour le
 * suivi des visites de la page Logs. Les deux ont divergé — quatre pages
 * (Dossiers, Secteurs techniciens, Absences, Créneaux E-RDV) n'étaient
 * tracées nulle part parce qu'on les avait ajoutées au menu seul, et deux
 * libellés ne se correspondaient plus. Le journal annonçait donc une
 * fréquentation fausse, et l'oubli était invisible : la trace ne part que si
 * le chemin figure dans la liste, sans rien dire quand il n'y est pas.
 *
 * Désormais le menu et le suivi lisent la même liste : ajouter une page ici
 * suffit, et on ne peut plus l'ajouter à l'un en l'oubliant dans l'autre.
 *
 * `admin` réserve l'entrée aux administrateurs. Le badge du nombre de comptes
 * rendus en attente reste au composant : il change à chaque chargement.
 */
const PAGES = [
  { path: '/',                  label: 'Tableau de bord',       icon: 'dashboard' },
  { path: '/dossier',           label: 'Dossiers',              icon: 'dossier' },
  { path: '/techniciens',       label: 'Techniciens',           icon: 'users' },
  { path: '/secteurs',          label: 'Secteurs techniciens',  icon: 'secteurs' },
  { path: '/absences',          label: 'Absences',              icon: 'absences' },
  { path: '/erdv',              label: 'Créneaux E-RDV',        icon: 'erdv' },
  { path: '/cr',                label: 'Comptes-rendus',        icon: 'docs' },
  { path: '/email-log',         label: 'Emails CR envoyés',     icon: 'emaillog' },
  { path: '/planning',          label: 'Planning',              icon: 'planning' },
  { path: '/planification',     label: 'Planification RDV',     icon: 'planif' },
  { path: '/suivi-cri',         label: 'Suivi CRI',             icon: 'cri' },
  { path: '/suivi-terrain',     label: 'Suivi Terrain',         icon: 'terrain' },
  { path: '/terrain',           label: 'GPS',                   icon: 'ousontils' },
  { path: '/malus',             label: 'Malus',                 icon: 'malus' },
  { path: '/portail-covage',    label: 'Portail Covage',        icon: 'guichet' },
  { path: '/devis-a-completer', label: 'Devis à compléter',     icon: 'devis' },
  { path: '/recherche',         label: 'Recherche',             icon: 'search' },
  { path: '/emails',            label: 'Boîte mail',            icon: 'mail' },
  { path: '/sav',               label: 'SAV Covage',            icon: 'sav' },
  { path: '/commande-ont',      label: 'Commande ONT',          icon: 'ont' },
  { path: '/agenda',            label: 'Agenda',                icon: 'agenda' },
  { path: '/pieces-jointes',    label: 'Pièces jointes',        icon: 'pj' },
  { path: '/workflow',          label: 'Workflows',             icon: 'workflow' },
  { path: '/kpi',               label: 'KPI Covage',            icon: 'kpi' },
  { path: '/qualifications',    label: 'Qualifications SLA',    icon: 'kpi' },
  { path: '/remunerations',     label: 'Rémunérations',         icon: 'remun' },
  { path: '/stock-ont',         label: 'Stock ONT',             icon: 'stock' },
  { path: '/import',            label: 'Import Commandes',      icon: 'import' },
  { path: '/stats',             label: 'Statistiques',          icon: 'stats' },
  { path: '/comptes',           label: 'Comptes',               icon: 'accounts', admin: true },
  { path: '/logs',              label: 'Logs activité',         icon: 'logs',     admin: true },
  { path: '/sms',               label: 'SMS / WhatsApp',        icon: 'sms',      admin: true },
  // Les paramètres s'ouvrent à tout le monde, mais la page ne montre à la
  // conduite d'activité que le thème de l'interface : choisir clair ou sombre
  // est une préférence d'affichage, elle ne se demande à personne.
  { path: '/settings',          label: 'Paramètres',            icon: 'settings' },
];

function Sidebar({ pendingCount }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, signOut } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const initials = admin?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'AD';
  const allowedPages = admin?.pages || null;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1');
  const [filtreMenu, setFiltreMenu] = useState('');

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar_collapsed', next ? '1' : '0');
  };

  const ALL_NAV = PAGES
    .filter(p => !p.admin || isAdmin)
    .map(p => (p.path === '/cr' ? { ...p, badge: pendingCount } : p));
  const autorises = isAdmin || !allowedPages
    ? ALL_NAV
    : ALL_NAV.filter(n => n.path === '/settings' || allowedPages.includes(n.path));
  // Le filtre ignore accents et casse : la page s'appelle « Rémunérations », on
  // tape « remuneration » et on veut la trouver.
  const sansAccents = v => String(v || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const requete = sansAccents(filtreMenu).trim();
  const navItems = requete
    ? autorises.filter(n => sansAccents(n.label).includes(requete) || sansAccents(n.path).includes(requete))
    : autorises;

  const W = collapsed ? 62 : 240;

  return (
    <div className="sidebar" style={{ width: W, minWidth: W, transition: 'width .2s ease, min-width .2s ease', overflow: 'hidden', position: 'relative' }}>
      {/* Logo */}
      <div className="sidebar-logo" style={{ padding: collapsed ? '18px 0' : undefined, justifyContent: collapsed ? 'center' : undefined, display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 10, justifyContent: collapsed ? 'center' : undefined, width: '100%' }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#fff', flexShrink: 0 }}>V</div>
          {!collapsed && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-.3px' }}>VALEA</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em' }}>Admin</div>
            </div>
          )}
        </div>
      </div>

      {/* Ce qui a echoue sans que personne ne regarde. Sous le logo, donc sur
          toutes les pages : une alerte qu'il faut aller chercher n'alerte
          personne. */}
      <Alertes collapsed={collapsed} />

      {/* Recherche de page */}
      {!collapsed && (
        <div style={{ padding: '8px 12px 2px' }}>
          <input
            value={filtreMenu}
            onChange={e => setFiltreMenu(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') setFiltreMenu('');
              // Entrée ouvre la première page trouvée : chercher puis cliquer
              // fait deux gestes là où un seul suffit.
              if (e.key === 'Enter' && navItems.length) { navigate(navItems[0].path); setFiltreMenu(''); }
            }}
            placeholder="Rechercher une page…"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '6px 9px', fontSize: 12,
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--text)',
            }}
          />
          {requete && (
            <div style={{ fontSize: 10, color: 'var(--text3)', padding: '4px 2px 0' }}>
              {navItems.length === 0
                ? 'aucune page'
                : navItems.length + ' page' + (navItems.length > 1 ? 's' : '') + ' — Entrée pour ouvrir'}
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="sidebar-nav" style={{ padding: collapsed ? '12px 6px' : undefined }}>
        {navItems.map(item => {
          const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <button
              key={item.path}
              className={`nav-item ${isActive ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
              title={collapsed ? item.label : undefined}
              style={{ justifyContent: collapsed ? 'center' : undefined, padding: collapsed ? '9px 0' : undefined }}
            >
              <span className="icon">{ICONS[item.icon]}</span>
              {!collapsed && item.label}
              {!collapsed && item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
              {collapsed && item.badge > 0 && (
                <span style={{ position: 'absolute', top: 4, right: 6, background: 'var(--accent)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 10 }}>{item.badge}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bouton collapse */}
      <button
        onClick={toggleCollapse}
        title={collapsed ? 'Agrandir le menu' : 'Réduire le menu'}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: collapsed ? '4px auto 8px' : '4px 8px 8px',
          width: collapsed ? 36 : '100%',
          padding: '6px 0',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: 'var(--bg3)',
          color: 'var(--text3)',
          cursor: 'pointer',
          fontSize: 13,
          transition: 'all .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--bg2)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.background = 'var(--bg3)'; }}
      >
        {collapsed ? '›' : '‹ Réduire'}
      </button>

      {/* Footer */}
      <div className="sidebar-footer" style={{ padding: collapsed ? '12px 6px' : undefined }}>
        <div className="sidebar-user" style={{ justifyContent: collapsed ? 'center' : undefined }}>
          <div className="avatar" title={collapsed ? (admin?.name || 'Admin') : undefined}>{initials}</div>
          {!collapsed && (
            <>
              <div className="sidebar-user-info">
                <div className="name">{admin?.name || 'Admin'}</div>
                <div className="role">{isAdmin ? 'Administrateur' : (admin?.role_name || 'Conducteur')}</div>
              </div>
              <button className="btn-ghost btn btn-sm" onClick={signOut} title="Déconnexion" style={{ marginLeft: 'auto', padding: 6 }}>
                <span style={{ width: 16, height: 16 }}>{ICONS.logout}</span>
              </button>
            </>
          )}
          {collapsed && (
            <button className="btn-ghost btn btn-sm" onClick={signOut} title="Déconnexion" style={{ padding: 4, marginTop: 6 }}>
              <span style={{ width: 16, height: 16, display: 'block' }}>{ICONS.logout}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// Le libellé sous lequel chaque page apparaît dans le journal : celui du
// menu, pris du même catalogue, pour que les deux ne puissent plus diverger.
const PAGE_LABELS = Object.fromEntries(PAGES.map(p => [p.path, p.label]));

// Trace de consultation alimentant la page Logs. La fiche d'un compte rendu
// est ramenée à /cr : sans cela, chaque dossier ouvert créerait son propre
// libellé et le journal deviendrait illisible.
function usePageTracker() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname.replace(/\/cr\/.*/, '/cr');
    const label = PAGE_LABELS[path];
    if (label) logPageVisit(label);
  }, [location.pathname]);
}

/**
 * La recherche de dossier, en haut de toutes les pages.
 *
 * Elle vivait sur la page « Dossiers » : pour chercher une référence, il
 * fallait d'abord y aller. Elle est désormais partout, et Ctrl+K la met sous
 * le curseur sans quitter des yeux ce qu'on fait.
 *
 * Les résultats s'affichent sous la barre : un clic ouvre la fiche. Entrée
 * ouvre la page « Dossiers » avec la même requête, pour qui veut la liste
 * entière et ses colonnes.
 */
function RechercheGlobale() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [cherche, setCherche] = useState(false);
  const champ = React.useRef(null);

  // Ctrl+K — ou Cmd+K — met le curseur dans la barre, d'où qu'on vienne.
  useEffect(() => {
    const au = e => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        champ.current?.focus();
        champ.current?.select();
      }
      if (e.key === 'Escape') setOuvert(false);
    };
    window.addEventListener('keydown', au);
    return () => window.removeEventListener('keydown', au);
  }, []);

  // On attend que la frappe se calme : sans ce délai, « O2610 » lance cinq
  // recherches dont quatre ne serviront à personne.
  useEffect(() => {
    const t = q.trim();
    if (t.length < 3) { setRes(null); setOuvert(false); return undefined; }
    const minuteur = setTimeout(() => {
      setCherche(true);
      rechercheFiche(t)
        .then(r => { setRes(r); setOuvert(true); })
        .catch(() => setRes(null))
        .finally(() => setCherche(false));
    }, 300);
    return () => clearTimeout(minuteur);
  }, [q]);

  const ouvrir = (id) => {
    setOuvert(false); setQ('');
    navigate('/dossier/' + id);
  };

  const soumettre = (e) => {
    e.preventDefault();
    const t = q.trim();
    if (t.length < 3) return;
    const items = (res && res.items) || [];
    if (items.length === 1) return ouvrir(items[0].id);
    setOuvert(false);
    navigate('/dossier?q=' + encodeURIComponent(t));
  };

  const items = (res && res.items) || [];

  return (
    <div className="barre-globale">
      <form onSubmit={soumettre} style={{ position: 'relative', width: '100%', maxWidth: 620 }}>
        <input ref={champ} value={q} onChange={e => setQ(e.target.value)}
          onFocus={() => items.length && setOuvert(true)}
          onBlur={() => setTimeout(() => setOuvert(false), 160)}
          placeholder="Rechercher un dossier — réf O, réf W, FI, VIA, IMB, adresse, client…"
          aria-label="Rechercher un dossier"
          style={{ width: '100%', padding: '8px 14px', fontSize: 13, borderRadius: 8,
                   border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)' }} />
        <span style={{ position: 'absolute', right: 11, top: 8, fontSize: 11, color: 'var(--text3)',
                       pointerEvents: 'none' }}>
          {cherche ? '…' : 'Ctrl+K'}
        </span>

        {ouvert && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 5, zIndex: 50,
                        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 9,
                        boxShadow: '0 10px 30px rgba(0,0,0,.28)', maxHeight: '60vh', overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: '11px 14px', fontSize: 12.5, color: 'var(--text3)' }}>
                Aucun dossier pour « {q.trim()} ».
              </div>
            ) : (
              <>
                {items.slice(0, 10).map(x => (
                  <button key={x.id} type="button" onMouseDown={() => ouvrir(x.id)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 0,
                             background: 'none', color: 'var(--text)', font: 'inherit',
                             padding: '8px 14px', cursor: 'pointer',
                             borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                      {x.ref_commande || x.ref_work_order}
                      <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 8 }}>
                        {x.stage}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                      {[x.client_final, [x.code_postal, x.ville].filter(Boolean).join(' ')]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </button>
                ))}
                {items.length > 10 && (
                  <div style={{ padding: '7px 14px', fontSize: 11.5, color: 'var(--text3)' }}>
                    {items.length - 10} autre{items.length - 10 > 1 ? 's' : ''} — Entrée pour la liste entière.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

function AdminLayoutInner() {
  const [pendingCount, setPendingCount] = useState(0);
  usePageTracker();
  useEffect(() => {
    // On pourrait fetcher le count ici
  }, []);
  return (
    <div className="layout">
      <Sidebar pendingCount={pendingCount} />
      <div className="main">
        <RechercheGlobale />
        <div className="main-scroll">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/techniciens" element={<Techniciens />} />
          <Route path="/cr" element={<CompteRendus onPendingCount={setPendingCount} />} />
          <Route path="/cr/:id" element={<CRDetail />} />
          <Route path="/email-log" element={<EmailLog />} />
          <Route path="/planning" element={<Planning />} />
          <Route path="/planification" element={<Planification />} />
          <Route path="/recherche" element={<Recherche />} />
          <Route path="/sav" element={<SAV />} />
          <Route path="/commande-ont" element={<CommandeONT />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/pieces-jointes" element={<PiecesJointes />} />
          <Route path="/workflow" element={<Workflow />} />
          <Route path="/kpi" element={<KPI />} />
          <Route path="/qualifications" element={<Qualifications />} />
          <Route path="/remunerations" element={<Remunerations />} />
          <Route path="/stock-ont" element={<StockONT />} />
          <Route path="/secteurs" element={<Secteurs />} />
          <Route path="/absences" element={<Absences />} />
          <Route path="/erdv" element={<ERDV />} />
          <Route path="/terrain" element={<Terrain />} />
          <Route path="/malus" element={<Malus />} />
          <Route path="/portail-covage" element={<PortailCovage />} />
          <Route path="/devis-a-completer" element={<DevisSansMontant />} />
          <Route path="/dossier" element={<Dossier />} />
          <Route path="/dossier/:id" element={<Dossier />} />
          <Route path="/import" element={<ImportCovage />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/comptes" element={<Comptes />} />
          <Route path="/logs" element={<Logs />} />
          <Route path="/suivi-cri" element={<SuiviCRI />} />
          <Route path="/suivi-terrain" element={<SuiviTerrain />} />
          <Route path="/sms" element={<SMS />} />
          <Route path="/emails" element={<Emails />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </div>
    </div>
  );
}

function AdminLayout() {
  return (
    <SettingsProvider>
      <AdminLayoutInner />
    </SettingsProvider>
  );
}

function PrivateRoute({ children }) {
  const { admin } = useAuth();
  return admin ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={<PrivateRoute><AdminLayout /></PrivateRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
